/**
 * Libellés d'écran du formulaire d'un concept (`EntreeForm`) —
 * « Un mot pour le code, un mot pour l'écran » (docs/VOCABULAIRE_ECRAN.md, § 3.1).
 *
 * Aucun mot du modèle (entrée, thésaurus, périmètre, mot-clé) ni clé technique
 * (`scopeNote`, `entree1`…) ; seule la clé de `ENTREE_FORM_SAVANT` — l'intro
 * de la section « Notes de travail », rangée
 * sous « Pour aller plus loin » — a le droit de dire « SKOS ». Les noms des
 * renvois (« Voir », « Voir aussi »…) vivent dans `RenvoiEditor`.
 * Garde-fou : `entreeFormLabels.test.ts` (contre `motsBannis`).
 */
export const ENTREE_FORM_LABELS = {
    nom: 'Nom du concept',
    definition: 'Définition',
    definitionPlaceholder: 'En une phrase, ce que ce concept désigne',
    plusLoin: 'Pour aller plus loin',
    // Visible replié : ne nomme pas la notation, qui n'apparaît qu'une fois
    // « Pour aller plus loin » déplié (EntreeForm.test, « replié, le DOM ne
    // dit notation nulle part »).
    plusLoinResume: 'Précisions, renvois, variantes et notes',
    precisions: 'Précisions',
    precisionsAide: 'Ce que vous en dites selon un auteur ou un contexte, chacune avec son nom.',
    ajouterPrecision: 'Ajouter une précision',
    nomPrecision: 'Nom de la précision',
    renommerPrecision: 'Renommer la précision',
    validerNomPrecision: 'Valider le nom',
    supprimerPrecision: 'Supprimer la précision',
    precisionPrise: 'Ce nom de précision est déjà pris',
    unRenommage: 'Renommez une précision à la fois',
    renvois: 'Renvois',
    variantesNotation: 'Variantes et notation',
    variantes: 'Variantes de recherche',
    variantesAide: 'Abréviations, sigles, fautes fréquentes : le repérage et la recherche les reconnaissent sans les afficher.',
    ajouterVariante: 'Ajouter une variante',
    ajouterVarianteAide: 'Appuyez sur Entrée pour ajouter (ex. IA, intell. artif., AI)',
    notation: 'Notation',
    notationAide: 'Un code de classement, si vous en utilisez un.',
    notationPlaceholder: 'Ex. 621.3, A.1.2, INFO-003',
    // Titre de section : ne nomme pas le standard, que seule l'intro cite
    // (décision du 28/09/2026, VOCABULAIRE_ECRAN.md § 3.1).
    notesTravail: 'Notes de travail',
    skosIntro: "Notes documentaires du standard W3C SKOS ; la note d'application accompagne le concept dans le dictionnaire de concepts.",
    scopeNote: "Note d'application",
    scopeNoteAide: 'Dans quel contexte employer ce concept, et dans lequel l’éviter.',
    historyNote: 'Note historique',
    historyNoteAide: 'Comment ce concept a évolué dans le vocabulaire.',
    editorialNote: 'Note éditoriale',
    editorialNoteAide: 'Note de travail pour les co-éditeurs : elle suit le concept dans les partages.',
} as const;

/** Nom accessible du bouton qui retire une variante (puce). */
export const retirerVariante = (label: string): string => `Retirer la variante ${label}`;

/** Clés de l'étage savant : seules à pouvoir dire « SKOS ». */
export const ENTREE_FORM_SAVANT: ReadonlyArray<keyof typeof ENTREE_FORM_LABELS> = ['skosIntro'];

/** « Précision 2 » : nom affiché d'une précision au nom par défaut. */
export const precisionLabel = (n: number): string => `Précision ${n}`;

/** « Renvois (3) » quand la section est remplie, « Renvois » sinon. */
export const countLabel = (label: string, n: number): string => (n > 0 ? `${label} (${n})` : label);

/**
 * Libellés des deux fenêtres qui portent le formulaire (`ModalEntree`,
 * `ModalEntreeShare`) : titres et pied. Un seul bouton principal selon le
 * mode — « Créer » sans identifiant, « Enregistrer » avec — et « Supprimer »
 * en modification seulement (décision du 27/09/2026, § 3.1).
 */
export const ENTREE_WINDOW_LABELS = {
    titreNouveau: 'Nouveau concept',
    titreModifier: 'Modifier le concept',
    titreNouveauPartage: 'Nouveau concept partagé',
    titreModifierPartage: 'Modifier le concept partagé',
    annuler: 'Annuler',
    creer: 'Créer',
    enregistrer: 'Enregistrer',
    supprimer: 'Supprimer',
    /** Description (attribut `title`) du bouton « Supprimer » du pied. */
    supprimerAide: 'Supprimer le concept',
    supprimerPartageAide: 'Supprimer le concept partagé',
} as const;

/**
 * Libellés de la confirmation qu'ouvre « Supprimer » dans les deux fenêtres
 * d'un concept (`EntreeDeleteConfirm`), décision du 28/09/2026 (§ 3.1). Même
 * famille que les dialogues de suppression du tableau des concepts et de la
 * fenêtre d'une note. Le nom est celui du concept tel qu'enregistré.
 */
export const ENTREE_DELETE_CONFIRM_LABELS = {
    titre: (nom: string): string => `Supprimer « ${nom} » ?`,
    titrePartage: (nom: string): string => `Supprimer le concept partagé « ${nom} » ?`,
    irreversible: 'Cette action est irréversible.',
    consequence: 'Le concept sera retiré des notes qui le citent.',
    consequencePartage: 'Le concept disparaîtra du vocabulaire partagé pour tous ses participants.',
    annuler: 'Annuler',
    confirmer: 'Supprimer définitivement',
} as const;

/**
 * Clé de comparaison d'un nom (concept ou précision) : trimé, casse ignorée
 * (français). Un doublon se juge toujours sur cette clé, dans les deux fenêtres.
 */
export const nameKey = (name: string | undefined): string => (name ?? '').trim().toLocaleLowerCase('fr');
